import React, { useState, useEffect } from 'react';
import { MapPin, Lightbulb, CalendarClock, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useJobs } from '../../hooks/useJobs';
import { generateWorkdays } from '../../utils/work-day-calculator';
import { isoDateString, dateFromInput, dateToInput } from '../../utils/date-utils';

export default function ExtendJobDialog({ 
  isOpen, 
  onClose, 
  job
}) {
  const { updateJob } = useJobs();
  const [additionalDays, setAdditionalDays] = useState(1);
  const [extensionDates, setExtensionDates] = useState(['']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAdditionalDays(1);
      setExtensionDates(['']);
      setError('');
    }
  }, [isOpen]);

  // Update extension dates array when number of days changes
  useEffect(() => {
    const currentLength = extensionDates.length;
    if (additionalDays > currentLength) {
      // Add more date inputs
      setExtensionDates([...extensionDates, ...Array(additionalDays - currentLength).fill('')]);
    } else if (additionalDays < currentLength) {
      // Remove extra date inputs
      setExtensionDates(extensionDates.slice(0, additionalDays));
    }
  }, [additionalDays]);

  function handleDateChange(index, value) {
    const newDates = [...extensionDates];
    newDates[index] = value;
    setExtensionDates(newDates);
  }

  async function handleExtend() {
    if (additionalDays < 1) {
      setError('Additional days must be at least 1');
      return;
    }

    // Check if all dates are filled
    const emptyDates = extensionDates.filter(d => !d);
    if (emptyDates.length > 0) {
      setError(`Please select dates for all ${additionalDays} extension day${additionalDays !== 1 ? 's' : ''}`);
      return;
    }

    setSaving(true);

    try {
      const currentWorkDays = job.workDays || [];
      
      // Create extension days from selected dates
      const extensionDays = extensionDates.map(dateStr => {
        const date = dateFromInput(dateStr);
        return {
          id: isoDateString(date),
          date: date,
          status: 'planned',
          note: 'Extension day',
          createdAt: new Date()
        };
      });

      const newWorkDays = [...currentWorkDays, ...extensionDays];
      
      // Sort by date
      newWorkDays.sort((a, b) => new Date(a.date) - new Date(b.date));

      const newDuration = newWorkDays.length;

      await updateJob(job.id, {
        durationDays: newDuration,
        workDays: newWorkDays,
        statusHistory: [
          ...(job.statusHistory || []),
          {
            status: job.status,
            timestamp: new Date(),
            note: `Extended by ${additionalDays} day${additionalDays !== 1 ? 's' : ''} (total: ${newDuration} days)`
          }
        ]
      });

      console.log('✅ Job extended successfully');
      onClose();
    } catch (error) {
      console.error('❌ Error extending job:', error);
      setError('Failed to extend job. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (!job) return null;

  const currentWorkDays = job.workDays || [];
  const currentDuration = currentWorkDays.length || job.durationDays || 1;
  const newDuration = currentDuration + additionalDays;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Extend Job Duration"
      size="medium"
    >
      <div style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Job Info */}
        <div style={{
          padding: '16px',
          background: 'rgba(139, 69, 19, 0.05)',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid rgba(139, 69, 19, 0.1)'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: 'var(--brand-brown)',
            marginBottom: '6px'
          }}>
            #{job.contractNumber} - {job.customerName}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            color: 'var(--text-secondary)'
          }}>
            <MapPin size={13} strokeWidth={2.25} /> {job.name}
          </div>
        </div>

        {/* Current Scheduled Days */}
        <div style={{
          padding: '16px',
          background: 'var(--bg-card)',
          border: '1px solid var(--cell-border)',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Current Scheduled Days ({currentDuration})
          </div>

          {currentWorkDays.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {currentWorkDays.map((day, index) => {
                const date = new Date(day.date);
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      border: '1px solid var(--cell-border)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'var(--brand-brown)',
                        color: 'white',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: '700'
                      }}>
                        {index + 1}
                      </div>
                      <div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--text-primary)'
                        }}>
                          {date.toLocaleDateString('en-US', { 
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        {day.note && (
                          <div style={{
                            fontSize: '12px',
                            color: 'var(--text-tertiary)',
                            marginTop: '2px'
                          }}>
                            {day.note}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      fontWeight: '600',
                      padding: '4px 8px',
                      background: 'var(--bg-card)',
                      borderRadius: '4px',
                      border: '1px solid var(--cell-border)'
                    }}>
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              textAlign: 'center'
            }}>
              No work days scheduled yet
            </div>
          )}
        </div>

        {/* Additional Days Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            How many days to add?
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={additionalDays}
            onChange={(e) => setAdditionalDays(parseInt(e.target.value) || 1)}
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '2px solid var(--cell-border)',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--brand-brown)';
              e.target.style.background = 'var(--brand-brown-tint-soft)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--cell-border)';
              e.target.style.background = 'var(--bg-tertiary)';
            }}
          />
        </div>

        {/* Extension Date Pickers - One for Each Day */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Choose Date for Each Extension Day
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {extensionDates.map((date, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: 'var(--bg-secondary)',
                borderRadius: '10px',
                border: '1px solid var(--cell-border)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--info)',
                  color: 'white',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>
                  +{index + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginBottom: '4px',
                    fontWeight: '600'
                  }}>
                    Extension Day {index + 1}
                  </div>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => handleDateChange(index, e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: error && !date ? '2px solid var(--status-text-bold-red)' : '2px solid var(--cell-border)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      if (!error) {
                        e.target.style.borderColor = 'var(--info)';
                        e.target.style.background = 'var(--status-soft-blue-bg)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!error) {
                        e.target.style.borderColor = 'var(--cell-border)';
                        e.target.style.background = 'var(--bg-tertiary)';
                      }
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            <Lightbulb size={12} strokeWidth={2.25} /> Choose the exact date for each extension day - you have full control
          </div>
        </div>

        {/* New Duration Preview */}
        <div style={{
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.05) 0%, rgba(139, 69, 19, 0.02) 100%)',
          border: '2px solid rgba(139, 69, 19, 0.2)',
          borderRadius: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{
                fontSize: '12px',
                color: 'var(--status-text-bold-amber)',
                fontWeight: '600',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                New Total Duration
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: '700',
                color: 'var(--brand-brown)'
              }}>
                {newDuration} day{newDuration !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ opacity: 0.2, display: 'flex' }}>
              <CalendarClock size={44} strokeWidth={1.5} />
            </div>
          </div>
          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid rgba(139, 69, 19, 0.1)',
            fontSize: '13px',
            color: 'var(--status-text-bold-amber)'
          }}>
            {currentDuration} current + {additionalDays} extension = {newDuration} total
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '12px 14px',
            background: 'var(--error-bg)',
            border: '2px solid var(--error-border)',
            borderRadius: '10px',
            fontSize: '13px',
            color: 'var(--status-text-bold-red)',
            marginBottom: '20px',
            fontWeight: '600'
          }}>
            <AlertTriangle size={14} strokeWidth={2.25} /> {error}
          </div>
        )}

        {/* Info Message */}
        <div style={{
          padding: '14px 16px',
          background: 'var(--status-soft-blue-bg)',
          border: '1px solid var(--status-pill-prod-bg)',
          borderRadius: '10px',
          fontSize: '13px',
          color: 'var(--status-text-bold-blue)',
          display: 'flex',
          gap: '10px'
        }}>
          <div style={{ fontSize: '16px' }}>ℹ️</div>
          <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              You can choose different dates for each extension day
            </div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              Extension days will appear on your calendar exactly where you place them
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid var(--cell-border)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: 'var(--bg-secondary)'
      }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleExtend}
          disabled={saving || additionalDays < 1 || extensionDates.some(d => !d)}
        >
          {saving ? 'Adding Extension...' : `Add ${additionalDays} Day${additionalDays !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </Modal>
  );
}